import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Fab,
  useTheme,
  alpha,
  Stack,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Add as AddIcon,
  FitnessCenter as FitnessCenterIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Scale as ScaleIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import WorkoutLogForm from '../components/WorkoutLogForm';
import WorkoutLogList from '../components/WorkoutLogList';
import PhysicalMeasurementForm from '../components/PhysicalMeasurementForm';
import PhysicalMeasurementList from '../components/PhysicalMeasurementList';
import MUIStatCard from '../components/ui/MUIStatCard';
import { isTrainerExtendedFeaturesEnabled } from '../utils/featureFlags';

const SportsDiaryPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [showMeasurementForm, setShowMeasurementForm] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const [stats, setStats] = useState({
    totalWorkouts: 0,
    totalHours: 0,
    avgCalories: 0,
    thisWeekWorkouts: 0,
    favoriteType: t('common:labels.na'),
    totalMeasurements: 0
  });
  
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Redirect trainers if extended features are disabled
  useEffect(() => {
    if (user?.role === 'trainer' && !isTrainerExtendedFeaturesEnabled()) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchStats();
    fetchChartData();
  }, [refreshTrigger]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch workout stats
      const workoutsResponse = await api.get('/workout-logs/stats');
      const workoutStats = workoutsResponse.data.data || {};
      
      // Fetch measurements count
      const measurementsResponse = await api.get('/physical-measurements');
      const measurements = measurementsResponse.data.data || [];
      
      setStats({
        totalWorkouts: workoutStats.totalWorkouts || 0,
        totalHours: Math.round((workoutStats.totalDuration || 0) / 60 * 10) / 10,
        avgCalories: Math.round(workoutStats.avgCalories || 0),
        thisWeekWorkouts: workoutStats.thisWeekWorkouts || 0,
        favoriteType: workoutStats.favoriteType || t('common:labels.na'),
        totalMeasurements: measurements.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      // Fetch workout trends
      const trendsResponse = await api.get('/workout-logs/trends?days=30');
      const trends = trendsResponse.data.data || [];
      
      // Format data for charts
      const formattedData = trends.map(item => ({
        date: new Date(item._id.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        workouts: item.workoutCount,
        duration: Math.round(item.totalDuration),
        calories: item.totalCalories || 0
      }));
      
      setChartData(formattedData);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const handleWorkoutSave = () => {
    setRefreshTrigger(prev => prev + 1);
    setShowWorkoutForm(false);
    setEditingWorkout(null);
  };

  const handleMeasurementSave = () => {
    setRefreshTrigger(prev => prev + 1);
    setShowMeasurementForm(false);
    setEditingMeasurement(null);
  };

  const handleEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setShowWorkoutForm(true);
  };

  const handleEditMeasurement = (measurement) => {
    setEditingMeasurement(measurement);
    setShowMeasurementForm(true);
  };

  const tabLabels = [
    { label: t('sportsDiary:tabs.overview'), icon: <AssignmentIcon /> },
    { label: t('sportsDiary:tabs.workouts'), icon: <FitnessCenterIcon /> },
    { label: t('sportsDiary:tabs.measurements'), icon: <ScaleIcon /> },
    { label: t('sportsDiary:tabs.analytics'), icon: <TimelineIcon /> }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FitnessCenterIcon sx={{ fontSize: 48, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
              {t('sportsDiary:title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('sportsDiary:subtitle', { name: user?.firstName })}
            </Typography>
          </Box>
        </Box>
        
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip 
            icon={<TrendingUpIcon />}
            label={t('sportsDiary:stats.totalWorkouts', { count: stats.totalWorkouts })}
            variant="outlined"
            color="primary"
          />
          <Chip 
            label={t('sportsDiary:stats.hoursTrained', { hours: stats.totalHours })}
            variant="outlined"
            color="secondary"
          />
          <Chip 
            label={t('sportsDiary:stats.favorite', { type: stats.favoriteType })}
            variant="outlined"
            color="success"
          />
        </Stack>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabLabels.map((tab, index) => (
            <Tab 
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box>
          {/* Overview Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MUIStatCard
                title={t('sportsDiary:stats.totalWorkoutsTitle')}
                value={stats.totalWorkouts}
                icon={<FitnessCenterIcon />}
                loading={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MUIStatCard
                title={t('sportsDiary:stats.trainingHours')}
                value={`${stats.totalHours}h`}
                icon={<TrendingUpIcon />}
                loading={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MUIStatCard
                title={t('sportsDiary:stats.avgCalories')}
                value={stats.avgCalories}
                icon={<TrendingUpIcon />}
                loading={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MUIStatCard
                title={t('sportsDiary:stats.thisWeek')}
                value={stats.thisWeekWorkouts}
                icon={<AssignmentIcon />}
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Quick Charts */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sportsDiary:charts.workoutFrequency')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="workouts" 
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, lg: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sportsDiary:overview.quickActions')}
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setShowWorkoutForm(true)}
                      fullWidth
                    >
                      {t('sportsDiary:actions.logWorkout')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ScaleIcon />}
                      onClick={() => setShowMeasurementForm(true)}
                      fullWidth
                    >
                      {t('sportsDiary:actions.addMeasurement')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TimelineIcon />}
                      onClick={() => setCurrentTab(3)}
                      fullWidth
                    >
                      {t('sportsDiary:actions.viewAnalytics')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {currentTab === 1 && (
        <Box>
          <WorkoutLogList 
            onEdit={handleEditWorkout}
            refreshTrigger={refreshTrigger}
          />
        </Box>
      )}

      {currentTab === 2 && (
        <Box>
          <PhysicalMeasurementList 
            onEdit={handleEditMeasurement}
            refreshTrigger={refreshTrigger}
          />
        </Box>
      )}

      {currentTab === 3 && (
        <Box>
          {/* Analytics Charts */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sportsDiary:charts.trainingDuration')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="duration" fill={theme.palette.secondary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sportsDiary:charts.caloriesBurned')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="calories" 
                        stroke={theme.palette.error.main}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Floating Action Buttons */}
      {currentTab === 1 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => setShowWorkoutForm(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {currentTab === 2 && (
        <Fab
          color="secondary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => setShowMeasurementForm(true)}
        >
          <ScaleIcon />
        </Fab>
      )}

      {/* Forms */}
      <WorkoutLogForm
        open={showWorkoutForm}
        onClose={() => {
          setShowWorkoutForm(false);
          setEditingWorkout(null);
        }}
        onSave={handleWorkoutSave}
        editingWorkout={editingWorkout}
      />

      <PhysicalMeasurementForm
        open={showMeasurementForm}
        onClose={() => {
          setShowMeasurementForm(false);
          setEditingMeasurement(null);
        }}
        onSave={handleMeasurementSave}
        editingMeasurement={editingMeasurement}
      />
    </Container>
  );
};

export default SportsDiaryPage;